import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Review } from '../types/storeItem'
import { getVisitTypeDisplayLabel } from '../constants/visitType'
import Avatar from './Avatar'
import { formatScoreOneDecimal } from '../utils/scoreFormat'
import ListPhoto from './ListPhoto'
import PrivateBadge from './PrivateBadge'

interface ReviewListCardProps {
  review: Review
}

const ReviewListCard: React.FC<ReviewListCardProps> = ({ review }) => {
  const navigate = useNavigate()

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const year = date.getFullYear()
    const month = date.getMonth() + 1
    const day = date.getDate()
    return `${year}년 ${month}월 ${day}일`
  }

  const handleCardClick = () => {
    navigate(`/review-detail/${review.id}`)
  }

  return (
    <div
      className="w-full flex flex-col cursor-pointer pb-4px"
      onClick={handleCardClick}
    >
      <div className="mt-8px w-full py-8px px-20px flex align-center gap-16px">
        <Avatar
          nickname={review.nickname || review.username?.trim() || review.userId}
          profileImage={review.profileImageUrl}
          size={47}
        />

        <div className="flex-1 flex flex-col items-start gap-4px justify-center">
          <p className="font-pretendard font-bold text-16px text-brown-80%-main text-left text-ellipsis-1 w-full">
            {review.username?.trim() || review.userId}
          </p>
        </div>

        {/* 점수 */}
        <p className="font-pretendard font-bold text-16px text-brown-80%-main text-right my-auto">
          {review.hideScore
            ? '? 점'
            : `${formatScoreOneDecimal(review.rating)} 점`}
        </p>
      </div>

      {/* 리뷰 이미지 */}
      {review.images && review.images.length > 0 && (
        <div className="mt-16px w-full px-20px flex flex-nowrap gap-8px overflow-x-auto overflow-y-hidden scrollbar-hide [overscroll-behavior-x:contain] [-webkit-overflow-scrolling:touch] [touch-action:pan-x_pan-y]">
          {review.images.map((image, imgIndex) => (
            <div
              key={imgIndex}
              className="shrink-0 w-128px h-160px bg-brandPrimary-5% rounded-16px overflow-hidden cursor-pointer"
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/review-photo/${review.id}/${imgIndex}`)
              }}
            >
              <ListPhoto
                src={image}
                alt={`기록 이미지 ${imgIndex + 1}`}
                className="w-full h-full object-cover pointer-events-none"
                draggable={false}
              />
            </div>
          ))}
        </div>
      )}

      {/* 리뷰 텍스트 */}
      {review.content && (
        <div className="w-full mt-16px px-20px">
          <p
            className={`
              text-style-body2
              text-textColorText2
              whitespace-pre-wrap
              break-words
            `}
          >
            {review.content.replace(/\r\n/g, '\n').replace(/\\n/g, '\n')}
          </p>
        </div>
      )}

      <div className="w-full mt-16px px-20px py-4px flex gap-8px">
        {/* 방문 날짜 */}
        <p className="flex-1 text-style-captionM text-textColorText3 text-left flex items-center">
          {review.isPublic === false && (
            <>
              <PrivateBadge />
              <span className="mx-5px">·</span>
            </>
          )}
          {formatDate(review.visitDate || review.createdAt)}
        </p>

        {/* 서비스 유형 */}
        <p className="text-style-captionM text-textColorText3 text-right">
          {getVisitTypeDisplayLabel(review.visitType)}
        </p>
      </div>
    </div>
  )
}

export default ReviewListCard
